// Readable, grouped names for the permission codes the server returns
// (presentation only: the server owns which codes exist and who holds them).
// A code missing here is still shown, under "Other", by its code.

type Text = Readonly<Record<'ar' | 'en', string>>;

export const PERMISSION_GROUPS: readonly {
  readonly key: string;
  readonly label: Text;
  readonly prefixes: readonly string[];
}[] = [
  { key: 'bookings', label: { en: 'Bookings', ar: 'الحجوزات' }, prefixes: ['booking.'] },
  { key: 'contracts', label: { en: 'Contracts', ar: 'العقود' }, prefixes: ['contract.'] },
  { key: 'customers', label: { en: 'Customers', ar: 'العملاء' }, prefixes: ['customer.'] },
  { key: 'money', label: { en: 'Money', ar: 'الأموال' }, prefixes: ['finance.'] },
  { key: 'expenses', label: { en: 'Expenses', ar: 'المصروفات' }, prefixes: ['expense.'] },
  {
    key: 'units',
    label: { en: 'Units and maintenance', ar: 'الوحدات والصيانة' },
    prefixes: ['maintenance.', 'property.', 'unit.', 'reference_data.'],
  },
  {
    key: 'reports',
    label: { en: 'Reports, exports and audit', ar: 'التقارير والتصدير والسجل' },
    prefixes: ['report.', 'export.', 'audit.'],
  },
  { key: 'team', label: { en: 'Team and permissions', ar: 'الفريق والصلاحيات' }, prefixes: ['team.'] },
];

export const PERMISSION_LABELS: Readonly<Record<string, Text>> = {
  'booking.view': { en: 'See bookings and availability', ar: 'رؤية الحجوزات والتوفر' },
  'booking.create': { en: 'Create bookings', ar: 'إنشاء الحجوزات' },
  'booking.edit': { en: 'Edit booking details', ar: 'تعديل تفاصيل الحجز' },
  'booking.date_change': { en: 'Change booking dates', ar: 'تغيير تواريخ الحجز' },
  'booking.price_change': { en: 'Change the agreed booking price', ar: 'تغيير سعر الحجز المتفق عليه' },
  'booking.extension': { en: 'Extend a booking', ar: 'تمديد الحجز' },
  'booking.unit_move': { en: 'Move a booking to another unit', ar: 'نقل الحجز إلى وحدة أخرى' },
  'booking.check_in': { en: 'Check in', ar: 'تسجيل الدخول' },
  'booking.check_out': { en: 'Check out', ar: 'تسجيل الخروج' },
  'booking.early_exit': { en: 'End a booking early', ar: 'إنهاء الحجز مبكرًا' },
  'booking.no_show': { en: 'Record a no-show', ar: 'تسجيل عدم الحضور' },
  'booking.cancel': { en: 'Cancel a booking', ar: 'إلغاء الحجز' },
  'booking.archive': { en: 'Archive and restore bookings', ar: 'أرشفة الحجوزات واستعادتها' },
  'contract.view': { en: 'See contracts', ar: 'رؤية العقود' },
  'contract.prepare': { en: 'Prepare a contract', ar: 'إعداد العقد' },
  'contract.issue': { en: 'Issue a contract', ar: 'إصدار العقد' },
  'contract.reissue': { en: 'Reissue a contract', ar: 'إعادة إصدار العقد' },
  'contract.send': { en: 'Send contracts for signature', ar: 'إرسال العقود للتوقيع' },
  'contract.resend': { en: 'Resend the signing link', ar: 'إعادة إرسال رابط التوقيع' },
  'contract.change_recipient': { en: 'Change the signing recipient', ar: 'تغيير مستلم التوقيع' },
  'contract.withdraw_eligibility': { en: 'Withdraw signing eligibility', ar: 'سحب أهلية التوقيع' },
  'contract.technical_evidence_view': { en: 'See technical signing evidence', ar: 'رؤية الأدلة التقنية للتوقيع' },
  'customer.basic_view': { en: 'Find and see customers', ar: 'البحث عن العملاء ورؤيتهم' },
  'customer.profile_edit': { en: 'Edit customer contact details', ar: 'تعديل بيانات التواصل للعميل' },
  'customer.identity_view': { en: 'See identity numbers', ar: 'رؤية أرقام الهوية' },
  'customer.identity_edit': { en: 'Edit legal and identity details', ar: 'تعديل البيانات النظامية والهوية' },
  'customer.documents_view': { en: 'See customer documents', ar: 'رؤية مستندات العميل' },
  'customer.documents_manage': { en: 'Manage customer documents', ar: 'إدارة مستندات العميل' },
  'customer.merge': { en: 'Merge duplicate customers', ar: 'دمج العملاء المكررين' },
  'customer.archive': { en: 'Archive and restore customers', ar: 'أرشفة العملاء واستعادتهم' },
  'finance.booking_view': { en: 'See booking money', ar: 'رؤية أموال الحجز' },
  'finance.rent_payment_record': { en: 'Record rent payments', ar: 'تسجيل دفعات الإيجار' },
  'finance.rent_refund': { en: 'Record rent refunds', ar: 'تسجيل استردادات الإيجار' },
  'finance.deposit_collection_record': { en: 'Record deposits', ar: 'تسجيل التأمينات' },
  'finance.deposit_refund': { en: 'Refund deposits', ar: 'استرداد التأمينات' },
  'finance.deposit_retained': { en: 'Retain a deposit', ar: 'احتجاز التأمين' },
  'finance.pending_manage': { en: 'Manage pending transactions', ar: 'إدارة العمليات المعلّقة' },
  'finance.noncash_verify': { en: 'Confirm bank transactions', ar: 'تأكيد العمليات البنكية' },
  'finance.reversal': { en: 'Reverse a confirmed transaction', ar: 'عكس عملية مؤكدة' },
  'finance.reports': { en: 'See financial reports and figures', ar: 'رؤية التقارير والأرقام المالية' },
  'expense.view': { en: 'See expenses', ar: 'رؤية المصروفات' },
  'expense.record': { en: 'Record expenses', ar: 'تسجيل المصروفات' },
  'expense.correct': { en: 'Correct an expense', ar: 'تصحيح مصروف' },
  'expense.verify': { en: 'Confirm expenses', ar: 'تأكيد المصروفات' },
  'expense.withdraw': { en: 'Withdraw an expense', ar: 'سحب مصروف' },
  'expense.reversal': { en: 'Reverse an expense', ar: 'عكس مصروف' },
  'expense.supplier_refund': { en: 'Record supplier refunds', ar: 'تسجيل استردادات الموردين' },
  'expense.report': { en: 'See expense reports', ar: 'رؤية تقارير المصروفات' },
  'maintenance.create_edit': { en: 'Place a unit under maintenance', ar: 'وضع الوحدة تحت الصيانة' },
  'maintenance.planning': { en: 'Change maintenance planning', ar: 'تغيير تخطيط الصيانة' },
  'maintenance.close': { en: 'Return a unit to service', ar: 'إعادة الوحدة للخدمة' },
  'maintenance.cancel_planned': { en: 'Cancel planned maintenance', ar: 'إلغاء الصيانة المخططة' },
  'property.setup': { en: 'Property setup (structural configuration)', ar: 'تهيئة العقار (الإعدادات الهيكلية)' },
  'property.reference_price': { en: 'Set reference prices', ar: 'تحديد الأسعار المرجعية' },
  'unit.archive': { en: 'Archive and restore units', ar: 'أرشفة الوحدات واستعادتها' },
  'reference_data.manage': { en: 'Manage reference data', ar: 'إدارة البيانات المرجعية' },
  'report.operational': { en: 'See operational reports', ar: 'رؤية التقارير التشغيلية' },
  'export.operational': { en: 'Export operational reports', ar: 'تصدير التقارير التشغيلية' },
  'export.financial': { en: 'Export financial reports', ar: 'تصدير التقارير المالية' },
  'audit.view': { en: 'See the audit log', ar: 'رؤية سجل التدقيق' },
  'team.view': { en: 'See the team', ar: 'رؤية الفريق' },
  'team.invite': { en: 'Add and invite staff', ar: 'إضافة الموظفين ودعوتهم' },
  'team.role_assign': { en: 'Change a role', ar: 'تغيير الدور' },
  'team.scope_assign': { en: 'Change a branch scope', ar: 'تغيير نطاق الفرع' },
  'team.permission_grant': { en: 'Grant a permission', ar: 'منح صلاحية' },
  'team.permission_revoke': { en: 'Revoke a permission', ar: 'سحب صلاحية' },
  'team.deactivate': { en: 'Deactivate and reactivate staff', ar: 'تعطيل الموظفين وإعادة تفعيلهم' },
  'team.mfa_reset': { en: 'Reset two-factor authentication', ar: 'إعادة ضبط التحقق الثنائي' },
};

export function permissionLabel(code: string, locale: 'ar' | 'en'): string {
  return PERMISSION_LABELS[code]?.[locale] ?? code;
}

// Groups rows in catalogue order; unknown codes land in a final "Other" group.
export function groupPermissions<T extends { readonly code: string }>(
  rows: readonly T[],
): { readonly key: string; readonly label: Text; readonly rows: readonly T[] }[] {
  const order = Object.keys(PERMISSION_LABELS);
  const rank = (code: string) => {
    const index = order.indexOf(code);
    return index === -1 ? order.length : index;
  };
  const groups = PERMISSION_GROUPS.map((group) => ({
    key: group.key,
    label: group.label,
    rows: rows
      .filter((row) => group.prefixes.some((prefix) => row.code.startsWith(prefix)))
      .sort((a, b) => rank(a.code) - rank(b.code)),
  }));
  const other = rows.filter((row) => !PERMISSION_GROUPS.some((g) => g.prefixes.some((p) => row.code.startsWith(p))));
  return [
    ...groups.filter((group) => group.rows.length > 0),
    ...(other.length ? [{ key: 'other', label: { en: 'Other', ar: 'أخرى' }, rows: other }] : []),
  ];
}
